import { default as React, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  sectionHeader: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  sectionDescription: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  divider: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  formControl: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  label: {
    fontWeight: 600,
    fontSize: '1rem',
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  helperText: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },
  errorText: {
    color: theme.palette.error.main,
  },
}));

interface TerraformOptions {
  terraform_repo_type: string;
  terraform_version: string;
  backend_type: string;
  provider: string;
  include_spacelift: boolean;
}

export const TerraformInfrastructureOptions = ({
  onChange,
  rawErrors,
  formData,
  schema,
}: FieldExtensionComponentProps<Record<string, any>>) => {
  const classes = useStyles();
  const hasErrors = rawErrors && rawErrors.length > 0;

  // Helper function to get backend type based on provider
  const getBackendTypeForProvider = (provider: string): string => {
    switch (provider) {
      case 'AWS':
        return 'S3';
      case 'Azure':
        return 'Azure Blob';
      case 'GCP':
      default:
        return 'GCS';
    }
  };

  // Initialize state with defaults
  const initialProvider = formData?.provider || 'GCP';
  const [options, setOptions] = useState<TerraformOptions>({
    terraform_repo_type: 'Infrastructure', // Fixed to Infrastructure only
    terraform_version: formData?.terraform_version || '1.5',
    backend_type:
      formData?.backend_type || getBackendTypeForProvider(initialProvider),
    provider: initialProvider,
    include_spacelift: true, // Always true, Spacelift included by default
  });

  // Update parent form data whenever options change
  useEffect(() => {
    onChange(options);
  }, [options, onChange]);

  const handleSelectChange =
    (field: keyof TerraformOptions) =>
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const value = event.target.value as string;

      // If changing provider, auto-update backend type
      if (field === 'provider') {
        setOptions(prev => ({
          ...prev,
          provider: value,
          backend_type: getBackendTypeForProvider(value),
        }));
      } else {
        setOptions(prev => ({
          ...prev,
          [field]: value,
        }));
      }
    };

  return (
    <Box className={classes.root}>
      {/* Section Header */}
      <Box className={classes.sectionHeader}>
        <Typography className={classes.sectionTitle}>
          Terraform Infrastructure Configuration
        </Typography>
        <Typography className={classes.sectionDescription}>
          Configure your Terraform infrastructure repository settings. Spacelift
          configuration will be included by default.
        </Typography>
      </Box>

      <Divider className={classes.divider} />

      {/* Cloud Provider */}
      <FormControl fullWidth className={classes.formControl}>
        <FormLabel className={classes.label}>Cloud Provider</FormLabel>
        <Select
          value={options.provider}
          onChange={handleSelectChange('provider')}
          variant="outlined"
        >
          <MenuItem value="GCP">GCP (Google Cloud Platform)</MenuItem>
          <MenuItem value="AWS">AWS (Amazon Web Services)</MenuItem>
          <MenuItem value="Azure">Azure (Microsoft)</MenuItem>
        </Select>
        <FormHelperText className={classes.helperText}>
          Select the primary cloud provider for this infrastructure
        </FormHelperText>
      </FormControl>

      {/* Backend Type (Auto-selected based on provider) */}
      <FormControl fullWidth className={classes.formControl}>
        <FormLabel className={classes.label}>Backend Type</FormLabel>
        <Select
          value={options.backend_type}
          onChange={handleSelectChange('backend_type')}
          variant="outlined"
          disabled
        >
          <MenuItem value="GCS">GCS (Google Cloud Storage)</MenuItem>
          <MenuItem value="S3">S3 (AWS)</MenuItem>
          <MenuItem value="Azure Blob">Azure Blob Storage</MenuItem>
        </Select>
        <FormHelperText className={classes.helperText}>
          Automatically selected based on cloud provider
        </FormHelperText>
      </FormControl>

      <Divider className={classes.divider} />

      {/* Terraform Version */}
      <FormControl fullWidth className={classes.formControl}>
        <FormLabel className={classes.label}>Terraform Version</FormLabel>
        <Select
          value={options.terraform_version}
          onChange={handleSelectChange('terraform_version')}
          variant="outlined"
        >
          <MenuItem value="1.3">1.3</MenuItem>
          <MenuItem value="1.4">1.4</MenuItem>
          <MenuItem value="1.5">1.5</MenuItem>
          <MenuItem value="1.6">1.6</MenuItem>
          <MenuItem value="1.7">1.7</MenuItem>
        </Select>
        <FormHelperText className={classes.helperText}>
          Select the Terraform version for this repository
        </FormHelperText>
      </FormControl>

      {hasErrors && (
        <FormHelperText className={classes.errorText}>
          {rawErrors?.join(', ')}
        </FormHelperText>
      )}
      {!hasErrors && schema.description && (
        <FormHelperText className={classes.helperText}>
          {schema.description}
        </FormHelperText>
      )}
    </Box>
  );
};

export const TerraformInfrastructureOptionsFieldExtension =
  scaffolderPlugin.provide(
    createScaffolderFieldExtension({
      name: 'TerraformInfrastructureOptions',
      component: TerraformInfrastructureOptions,
    }),
  );
